'use client';
import {
  BestBuy,
  BestCategoriesCarousel,
  Carousel,
  CategoryCard,
  MainPageCategories,
  NewArrival,
  PopularBrands,
  PopularCard,
  SharedCarousel,
  TradeIn,
} from '@/components';
///////////////////////////////////////////////////////////////
////////// imported generated query //////////////////////
import {
  AddFavoriteMutation,
  AddVariantToComparisonMutation,
  CategorySortField,
  CategorySortingInput,
  ClearComparisonMutation,
  ClearFavoritesMutation,
  HomePageSortField,
  OrderDirection,
  ProductVariantSortingInput,
  useAddFavoriteMutation,
  useAddVariantToComparisonMutation,
  useClearComparisonMutation,
  useClearFavoritesMutation,
  useDraftOrderDeleteMutation,
  useDraftOrderQuery,
  usePopularCategoriesQuery,
  useProductVariantsQuery,
  useTopProductCategoriesQuery,
  useTopProductsQuery,
  useUserDraftOrdersQuery,
  useUserFavoritesQuery,
} from '@/graphql';
//////////////////////////////////////////////////////////

import { useCityStore } from '@/zustand/cityStore';
import comparisonStore from '@/zustand/comparison';
import { favoritesStore } from '@/zustand/favourite';
import { useGuestStore } from '@/zustand/guestStore';
import useUserStore from '@/zustand/useUser';
import { useCallback, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Button } from 'rsuite';

import styles from './main-content.module.scss';
import { useLocale, useTranslations } from 'next-intl';
import { mapEdgesToItems } from '@/helpers/maps';
import {
  NestedTranslatableItem,
  TranslatableItem,
  useNestedTranslatedData,
} from '@/hooks/useTranslatedData';
import { useLanguageCode } from '@/hooks/useLanguageCode';
type Props = {};

const MainPageContent = (props: Props) => {
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showSkeletons, setShowSkeletons] = useState(false);
  const userData = useUserStore((user: any) => user?.user);
  const isLoggedIn = Boolean(userData?.id);
  const orderId = useGuestStore(state => state.guestOrderId);
  const comparisons = comparisonStore(e => e.comparison);
  const setComparison = comparisonStore(e => e.setComparison);
  const { setFavorites } = favoritesStore(favorite => favorite);
  const [productsSortBy] = useState<ProductVariantSortingInput | null>(null);
  const locale = useLocale();

  const [categorySortBy] = useState<CategorySortingInput>({
    field: CategorySortField.LEVEL,
    direction: OrderDirection.ASC,
  });
  const t = useTranslations('General');
  const languageCode = useLanguageCode();

  const { selectedCity } = useCityStore();

  const guestComparisonListArray = useGuestStore(state => state.comparisonList);

  const guestFavoritesListArray = useGuestStore(state => state.favoriteList);

  const { data: favoriteData, refetch: refetchFavoriteData } =
    useUserFavoritesQuery({
      variables: { first: 10, sortBy: productsSortBy },
      context: {
        requiresAuth: true,
      },
    });

  const { data: categoryData, loading: categoryLoading } =
    useTopProductCategoriesQuery({
      variables: {
        first: 10,
        level: 1,
        sortBy: categorySortBy,
        languageCode: languageCode,
      },
    });

  const handleSuccessComparison = (data: AddVariantToComparisonMutation) => {
    if (data?.addVariantToComparison?.errors?.length === 0) {
      setComparison(data?.addVariantToComparison?.user?.comparison);
    }
  };
  const handleSuccess = (data: AddFavoriteMutation) => {
    if (data?.addFavorite?.errors?.length === 0) {
      setFavorites(data?.addFavorite?.user?.favorites?.edges || []);
    }
  };
  const [create] = useAddFavoriteMutation({
    onCompleted: handleSuccess,
    context: {
      requiresAuth: true,
    },
  });

  const [createComparison] = useAddVariantToComparisonMutation({
    onCompleted: handleSuccessComparison,
    context: {
      requiresAuth: true,
    },
  });

  const {
    data: initialTopData,
    loading: initialLoading,
    fetchMore,
    refetch,
  } = useTopProductsQuery({
    variables: {
      first: 10,
      filter: activeCategoryId ? { category: activeCategoryId } : {},
      sortBy: {
        field: HomePageSortField.AUTO,
        direction: OrderDirection.DESC,
      },
      languageCode: languageCode,
    },
  });

  useEffect(() => {
    if (initialTopData?.topProducts) {
      setProducts(initialTopData?.topProducts?.edges || []);
      setCursor(initialTopData?.topProducts?.pageInfo?.endCursor || null);
      setHasMore(initialTopData?.topProducts?.pageInfo?.hasNextPage || false);
    }
  }, [initialTopData]);

  const { data: popularCategoriesData, loading: categoriesLoading } =
    usePopularCategoriesQuery({
      variables: {
        first: 12,
        languageCode: languageCode,
      },
    });

  const {
    data: draftOrdersData,
    refetch: refetchDraftOrders,
    loading: isUserLoading,
  } = useUserDraftOrdersQuery({
    variables: {
      userId: userData?.id,
    },
  });

  const {
    data: guestdraftOrdersData,
    refetch: guestrefetchDraftOrders,
    loading: isGuestLoaiding,
  } = useDraftOrderQuery({
    variables: {
      id: orderId || '',
    },
  });

  const { data: guestFavoriteListData, loading: IsGuestFavoriteLoading } =
    useProductVariantsQuery({
      variables: {
        ids: (guestFavoritesListArray || []).filter(Boolean),
        first:
          guestFavoritesListArray.length === 0
            ? 10
            : guestFavoritesListArray.length,
        city: selectedCity,
        languageCode: languageCode,
      },
    });

  const handleClearComparison = (data: ClearComparisonMutation) => {
    if (data?.clearComparison?.errors?.length === 0) {
      setComparison(data.clearComparison?.user?.comparison);
    }
  };
  const handleClearFavorite = (data: ClearFavoritesMutation) => {
    if (data?.clearFavorites?.errors?.length === 0) {
      setComparison(data.clearFavorites?.user?.favorites);
    }
  };
  const [clearComparison] = useClearComparisonMutation({
    onCompleted: handleClearComparison,
    context: {
      requiresAuth: true,
    },
  });
  const [clearFavorite] = useClearFavoritesMutation({
    onCompleted: handleClearFavorite,
    context: {
      requiresAuth: true,
    },
  });

  const [deleteDraftOrderMutation] = useDraftOrderDeleteMutation();

  const loadMoreProducts = useCallback(async () => {
    if (!hasMore || loadingMore) return;

    setLoadingMore(true);
    try {
      const result = await fetchMore({
        variables: {
          first: 10,
          after: cursor,
          filter: activeCategoryId ? { category: activeCategoryId } : {},
          sortBy: {
            field: HomePageSortField.AUTO,
            direction: OrderDirection.DESC,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          const existingEdges = prev.topProducts?.edges || [];
          const newEdges = fetchMoreResult.topProducts?.edges || [];

          // remove duplicate products by ID
          const mergedEdgesMap = new Map();
          [...existingEdges, ...newEdges].forEach(edge => {
            if (edge?.node?.id) {
              mergedEdgesMap.set(edge.node.id, edge);
            }
          });

          const mergedEdges = Array.from(mergedEdgesMap.values());
          const fetchMoreResultPageInfo = fetchMoreResult.topProducts?.pageInfo;

          return {
            ...prev,
            topProducts: {
              __typename: 'TopProductCountableConnection',
              totalCount: fetchMoreResult.topProducts?.totalCount || 0,
              edges: mergedEdges,
              pageInfo: {
                __typename: 'PageInfo',
                endCursor: fetchMoreResultPageInfo?.endCursor || null,
                hasNextPage: fetchMoreResultPageInfo?.hasNextPage || false,
                hasPreviousPage:
                  fetchMoreResultPageInfo?.hasPreviousPage || false,
                startCursor: fetchMoreResultPageInfo?.startCursor || null,
              },
            },
          };
        },
      });

      const topProducts = result?.data?.topProducts;

      if (topProducts) {
        setProducts(prev => {
          const existingIds = new Set(prev.map(p => p.node.id));
          const newEdges = (topProducts.edges || []).filter(
            p => !existingIds.has(p.node.id)
          );
          return [...prev, ...newEdges];
        });
        setCursor(topProducts.pageInfo?.endCursor || null);
        setHasMore(topProducts.pageInfo?.hasNextPage || false);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching more products:', error);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, cursor, fetchMore, activeCategoryId, loadingMore]);

  const handleCollapse = useCallback(async () => {
    setLoadingMore(true);
    setCursor(null);
    setHasMore(true);

    try {
      await refetch({
        first: 10,
        filter: activeCategoryId ? { category: activeCategoryId } : {},
        sortBy: {
          field: HomePageSortField.AUTO,
          direction: OrderDirection.DESC,
        },
        languageCode: languageCode,
      });
    } catch (error) {
      console.error('Error resetting to initial products:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [refetch, activeCategoryId]);

  const topProducts = products;

  const mappedData: NestedTranslatableItem[] =
    mapEdgesToItems({
      edges: topProducts,
    }) || [];

  const translatedTopProducts = useNestedTranslatedData(
    mappedData,
    ['name'],
    ['brand', 'category', 'variant']
  );

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (initialLoading) {
      setShowSkeletons(true);
    } else {
      timer = setTimeout(() => {
        setShowSkeletons(false);
      }, 200);
    }

    return () => clearTimeout(timer);
  }, [initialLoading]);

  useEffect(() => {
    const isGuestCart = Boolean(guestdraftOrdersData?.draftOrder);

    guestrefetchDraftOrders();

    if (isLoggedIn && !isGuestLoaiding && isGuestCart === true) {
      mergeGuestCartToUser();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const shouldMerge =
      isLoggedIn &&
      !IsGuestFavoriteLoading &&
      guestComparisonListArray.length > 0;

    if (shouldMerge) {
      mergeGuestComparisonToUser();
    }
  }, [isLoggedIn, IsGuestFavoriteLoading, guestComparisonListArray]);

  useEffect(() => {
    const isGuestFavorites = guestFavoritesListArray.length > 0;

    if (isLoggedIn && !IsGuestFavoriteLoading && isGuestFavorites) {
      mergeGuestFavoritesToUser();
    }
  }, [isLoggedIn, guestFavoritesListArray, IsGuestFavoriteLoading]);

  const mergeGuestComparisonToUser = async () => {
    try {
      const hasUserComparison = comparisons?.length > 0;

      if (hasUserComparison) {
        // Remove current user's comparison list
        const allIds = comparisons
          ?.flatMap((group: any) =>
            group?.children?.map((item: any) => item.id)
          )
          .filter(Boolean);

        if (allIds.length > 0) {
          await clearComparison({ variables: { ids: allIds } });
        }
      }

      if (guestComparisonListArray.length > 0) {
        // Add guest comparison items to user's list
        const productVariants =
          guestFavoriteListData?.productVariants?.edges || [];
        const guestComparisons = productVariants
          .map(edge => edge.node)
          .filter(Boolean);

        if (guestComparisons.length > 0) {
          for (const variant of guestComparisons) {
            await createComparison({ variables: { variant: variant.id } });
          }

          setComparison(guestComparisons);
        }

        useGuestStore.getState().clearComparison();
        useGuestStore.getState().clearComparisonDisplayData();
      }
    } catch (error) {
      console.error('Error merging guest comparison to user:', error);
    }
  };

  const mergeGuestCartToUser = async () => {
    const guestLines = guestdraftOrdersData?.draftOrder?.lines.map(line => ({
      variantId: line.productVariantId!,
      quantity: line.quantity || 1,
    }));

    const hasUserCart = draftOrdersData?.userDraftOrders?.length;

    try {
      if (!isUserLoading && guestLines?.length) {
        if (hasUserCart) {
          console.log('User has cart. Deleting old cart...');
          const existingOrderId = draftOrdersData?.userDraftOrders?.[0]?.id;
          if (existingOrderId) {
            await deleteDraftOrderMutation({
              variables: { id: existingOrderId },
            });
          }
        } else {
          console.log('No user cart. Proceeding to add guest cart...');
        }

        if (guestdraftOrdersData?.draftOrder?.id) {
          await deleteDraftOrderMutation({
            variables: { id: guestdraftOrdersData.draftOrder.id },
          });
          console.log('Guest draft order deleted');
        }
        useGuestStore.getState().clearGuestOrderId();
        refetchDraftOrders();
      }
    } catch (error) {
      console.error('Error merging guest cart:', error);
    }
  };

  const mergeGuestFavoritesToUser = async () => {
    const hasFavorites = favoriteData?.me?.favorites?.edges?.length ?? 0;
    const productVariants = guestFavoriteListData?.productVariants?.edges || [];

    if (!guestFavoriteListData || !productVariants.length) return;

    try {
      if (favoriteData?.me && hasFavorites > 0) {
        await clearFavorite();
      }

      const transformedData = productVariants
        .map(edge => edge?.node)
        .filter(Boolean);

      for (const variant of transformedData) {
        await create({ variables: { variant: variant.id } });
      }

      // Clear guest store
      useGuestStore.getState().clearFavorite();
      useGuestStore.getState().clearFavoriteDisplayData();

      refetchFavoriteData();
    } catch (error) {
      console.error('Failed to merge guest favorites:', error);
    }
  };

  const mappedPopularCategories: TranslatableItem[] =
    mapEdgesToItems(popularCategoriesData?.popularCategories) || [];
  const translatedPopularCategories = useNestedTranslatedData(
    mappedPopularCategories,
    ['name'],
    ['category']
  );

  return (
    <main className={styles.contentWrapper}>
      <div className={styles.navigationAndCarousel}>
        <MainPageCategories />
        <Carousel />
      </div>
      <div className={styles.bestCategories}>
        <div className={styles.bestCategoriesTabs}>
          {!!categoryData?.topProductCategories?.edges?.length && (
            <>
              <h2>{t('best-category')}</h2>
              <BestCategoriesCarousel
                categoryData={categoryData}
                categoryLoading={categoryLoading}
                activeCategoryId={activeCategoryId}
                setActiveCategoryId={setActiveCategoryId}
              />
            </>
          )}
          <div
            className={`${styles.categoryCards} ${
              topProducts && topProducts?.length < 5
                ? styles.categoryCardsOverride
                : ''
            }`}
          >
            {showSkeletons || initialLoading
              ? Array.from({ length: 10 }).map((_, index) => (
                  <div key={index} className={`${styles.skeleton}`}>
                    <Skeleton />
                  </div>
                ))
              : translatedTopProducts?.map(p => {
                  const labels =
                    p?.variant?.labels?.edges?.map((edge: any) => edge?.node) ||
                    [];

                  return (
                    <CategoryCard
                      id={p?.variant?.id}
                      categorySlug={p?.variant?.product?.category?.slug}
                      slug={p?.variant?.product?.slug}
                      key={p?.variant?.id}
                      productId={p?.variant?.product?.id}
                      image={p?.variant?.media[0]?.url}
                      productType={
                        locale === 'ru'
                          ? p?.variant.product?.category?.name
                          : p?.variant.product?.category.translation
                            ? p?.variant.product?.category.translation.name
                            : p?.variant.product?.category?.name
                      }
                      availability={p?.variant?.availability}
                      labels={labels}
                      title={p?.variant?.name}
                      pricePerMonth={
                        p?.variant?.pricing?.installmentPrice12?.amount
                      }
                      price={p?.variant?.pricing?.price?.net?.amount ?? 0}
                      priceUndiscounted={
                        p?.variant?.pricing?.priceUndiscounted?.net?.amount ?? 0
                      }
                      discount={p?.variant?.pricing?.discount?.net?.amount ?? 0}
                      bonus={p?.variant?.pricing?.bonusesAmount}
                      className={
                        topProducts && topProducts?.length < 5
                          ? styles.categoryCard
                          : ''
                      }
                    />
                  );
                })}
          </div>
          {hasMore && (
            <Button
              type="button"
              className={styles.moreCards}
              onClick={loadMoreProducts}
              disabled={loadingMore}
            >
              {loadingMore ? t('loading') : t('show-more')}
            </Button>
          )}
          {!hasMore && !activeCategoryId && (
            <Button
              type="button"
              className={styles.moreCards}
              onClick={handleCollapse}
              disabled={loadingMore}
            >
              {loadingMore ? t('loading') : t('collapse')}
            </Button>
          )}
        </div>
      </div>

      <div className={styles.cardsSection}>
        <NewArrival title={t('new')} />
      </div>
      {popularCategoriesData?.popularCategories?.edges &&
      popularCategoriesData?.popularCategories?.edges?.length > 0 ? (
        <div className={styles.popularCategories}>
          <h2>{t('popular-category')}</h2>
          <div className={styles.cardsWrapper}>
            {categoriesLoading
              ? Array.from({ length: 12 }).map((_, index) => (
                  <div key={index} className={`${styles.skeletonPopular}`}>
                    <Skeleton />
                  </div>
                ))
              : translatedPopularCategories.map((product: any) => {
                  return (
                    <PopularCard
                      key={product?.id}
                      title={product?.category?.name}
                      image={product?.category?.icon}
                      id={product?.category?.id}
                      slug={product?.category?.slug}
                    />
                  );
                })}
          </div>
        </div>
      ) : null}
      <div className={styles.cardsSection}>
        <TradeIn />
      </div>
      <div className={styles.cardsSection}>
        <BestBuy title={t('advantage-purchase')} />
      </div>
      <div>
        <SharedCarousel sliderProgressDisable={false} showMoreButton={true} />
      </div>

      <div className={styles.popularBrands}>
        <PopularBrands />
      </div>
    </main>
  );
};

export default MainPageContent;
